import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Navbar from "./common/Navbar.jsx";
import Footer from "./common/Footer.jsx";
import { useCart } from "../contexts/CartContext.jsx";
import "../styles/tailwind-bridge.css";

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="flex justify-center items-center space-x-2 py-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
      >
        <i className="fa-solid fa-chevron-left"></i>
        <span>Previous</span>
      </button>

      <div className="flex space-x-1">
        {getVisiblePages().map((page, index) =>
          page === "..." ? (
            <span key={index} className="px-3 py-2 text-gray-500">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                currentPage === page
                  ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {page}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
      >
        <span>Next</span>
        <i className="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  );
};

const SearchBar = ({ onSearch }) => {
  const [searchValue, setSearchValue] = useState("");

  const handleSearch = (value) => {
    setSearchValue(value);
    onSearch(value);
  };

  const clearSearch = () => {
    setSearchValue("");
    onSearch("");
  };

  return (
    <div className="relative max-w-md mx-auto">
      <div className="relative">
        <input
          type="search"
          placeholder="Search products..."
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full pl-12 pr-10 py-3 bg-white/90 backdrop-blur-sm border border-white/30 rounded-full text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400 transition-all duration-300"
        />
        <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500"></i>
        {searchValue && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
          ></button>
        )}
      </div>
    </div>
  );
};

const ImageLoader = ({ src, alt, className }) => {
  const [isLoading, setIsLoading] = useState(true);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
  };

  return (
    <div className={`image-loader-container ${isLoading ? "loading" : ""}`}>
      {isLoading && <div className="image-loader-spinner"></div>}
      <img
        src={src}
        alt={alt}
        className={`${className} ${isLoading ? "hidden" : ""}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};

const TrendingProducts = ({ products, onProductClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const trendingProducts = products.slice(0, 10);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(
        (prevIndex) => (prevIndex + 1) % (trendingProducts.length - 4)
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [trendingProducts.length]);

  return (
    <div className="trending-container">
      <h2>Trending Products</h2>
      <div className="trending-carousel">
        {trendingProducts
          .slice(currentIndex, currentIndex + 5)
          .map((product, index) => (
            <div
              key={product.id}
              className={`trending-item ${
                index === 2 ? "trending-center" : ""
              }`}
              onClick={() => {
                navigate(`/market/${product.id}`);
                onProductClick(product);
              }}
            >
              <ImageLoader
                src={`${import.meta.env.VITE_API_URL}/${product.image1}`}
                alt={product.name}
                className="trending-image"
              />
              <div className="trending-info">
                <h3>{product.name}</h3>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, onClick }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [isAdded, setIsAdded] = useState(false);
  const oldPrice = Math.round(product.price * 1.25);

  const handleClick = () => {
    navigate(`/market/${product.id}`);
    onClick(product);
  };
  const handleAddToCart = (e) => {
    e.stopPropagation();
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      image1: product.image1,
      stock: product.Quantity,
      quantity: 1, // 1 qté par défaut
    };
    addToCart(cartItem);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
    alert(`${product.name} added to cart!`);
  };

  return (
    <div
      className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 overflow-hidden cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative aspect-square overflow-hidden">
        <ImageLoader
          src={`${import.meta.env.VITE_API_URL}/${product.image1}`}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Discount Badge */}
        <div className="absolute top-4 left-4">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
            {Math.round((1 - product.price / oldPrice) * 100)}% OFF
          </div>
        </div>
      </div>

      <div className="p-2 px-4">
        <h3 className="font-bold text-lg mb-2 text-gray-900 line-clamp-2 group-hover:text-cyan-600 transition-colors">
          {product.name}
        </h3>

        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col xl:flex-row gap-1 xl:gap-5 xl:items-center">
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
              {product.price.toLocaleString()} Fbu
            </span>
            <span className="text-sm text-red-400 line-through">
              {oldPrice.toLocaleString()} Fbu
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-600 flex items-center">
            <i className="fa-solid fa-box mr-1 text-green-500"></i>
            {product.Quantity} in stock
          </span>
        </div>

        <div className="flex space-x-2">
          <button className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-2 px-1 xl:px-4 rounded-full font-semibold hover:scale-105 transition-transform duration-300 flex items-center justify-center space-x-2">
            <span>View</span>
          </button>
          <button
            onClick={handleAddToCart}
            disabled={isAdded}
            id="cartIcon"
            className={`w-12 h-10 rounded-full font-semibold transition-all duration-300 flex items-center justify-center cartIcon ${
              isAdded
                ? "bg-green-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            title={isAdded ? "Added to cart!" : "Add to cart"}
          >
            <i
              className={`fa-solid  ${
                isAdded ? "fa-check" : "fa-shopping-cart"
              }`}
            ></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProductDetail = ({ product, isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quantity, setQuantity] = useState(1);
  const [delivery, setDelivery] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const { addToCart } = useCart();

  // Calculate old price
  const oldPrice = product ? Math.round(product.price * 1.25) : 0; // 25% higher than current price

  useEffect(() => {
    if (isOpen && product) {
      setMainImage(product.image1);
      setQuantity(1);
      setDelivery(false);
      setError(null);
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const handleImageClick = (imagePath) => {
    setMainImage(imagePath);
  };

  const calculateTotalPrice = () => {
    const basePrice = product.price * quantity;
    return delivery ? basePrice * 1.05 : basePrice; // Add 5% for delivery
  };

  const handleClose = () => {
    // Check if we're in a direct product URL and navigate back to market main page
    if (location.pathname.includes(`/market/${product.id}`)) {
      navigate("/market");
    }
    onClose();
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      image1: product.image1,
      quantity,
    };
    addToCart(cartItem);
    alert(`${product.name} added to cart!`);
    handleClose();
  };

  return (
    <div
      className="fixed inset-0 top-14 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 m-0"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-2 relative">
          <h2 className="text-2xl font-bold pr-12 text-white">
            {product.name}
          </h2>
          <button
            onClick={handleClose}
            className="absolute top-2 right-4 w-8 h-8 bg-white/50 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <i className="fa-solid fa-times text-xl text-red-500"></i>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-80px)] overflow-y-auto">
          {/* Images Section */}
          <div className="lg:w-1/2 p-6">
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden">
                <ImageLoader
                  src={`${import.meta.env.VITE_API_URL}/${mainImage}`}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Thumbnail Images */}
              <div className="flex space-x-3">
                <div
                  className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden cursor-pointer border-2 border-transparent hover:border-cyan-400 transition-colors"
                  onClick={() => handleImageClick(product.image1)}
                >
                  <ImageLoader
                    src={`${import.meta.env.VITE_API_URL}/${product.image1}`}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {product.image2 && (
                  <div
                    className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden cursor-pointer border-2 border-transparent hover:border-cyan-400 transition-colors"
                    onClick={() => handleImageClick(product.image2)}
                  >
                    <ImageLoader
                      src={`${import.meta.env.VITE_API_URL}/${product.image2}`}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Product Info Section */}
          <div className="lg:w-1/2 p-6 space-y-2 h-content">
            {/* Price Section */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-100 rounded-2xl py-2 px-6">
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-600 font-medium">Price</span>
                <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  {Math.round((1 - product.price / oldPrice) * 100)}% OFF
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-gray-900">
                  {product.price.toLocaleString()} Fbu
                </span>
                <span className="text-lg text-red-500 line-through">
                  {oldPrice.toLocaleString()} Fbu
                </span>
              </div>
            </div>

            {/* Product Details */}
            <div className="space-y-4">
              <div className="flex items-center justify-between py-1 border-b border-gray-200">
                <span className="font-medium text-gray-600">Brand</span>
                <span className="text-gray-900">{product.Brand}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-gray-200">
                <span className="font-medium text-gray-600">Category</span>
                <span className="text-gray-900">{product.category}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-gray-200">
                <span className="font-medium text-gray-600">Available</span>
                <span className="text-green-600 font-semibold">
                  {product.Quantity} Items
                </span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-gray-200">
                <span className="font-medium text-gray-600">Sold</span>
                <span className="text-blue-900">
                  {product.number_by_sell} Items
                </span>
              </div>
            </div>

            {/* Product Description */}
            <div className="bg-gray-50 rounded-2xl p-4 py-2">
              <h4 className="font-semibold text-gray-900 mb-1">
                Product Details
              </h4>
              <p className="text-gray-700 leading-relaxed">{product.Details}</p>
            </div>

            {/* Quantity Selector */}
            <div className="bg-white border border-gray-200 rounded-2xl p-2">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Quantity
              </label>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <i className="fa-solid fa-minus text-sm text-red-500"></i>
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.Quantity}
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(
                      Math.min(
                        Math.max(1, Number(e.target.value)),
                        product.Quantity
                      )
                    )
                  }
                  className="w-20 text-center border border-gray-300 rounded-lg py-2 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                />
                <button
                  onClick={() =>
                    setQuantity(Math.min(product.Quantity, quantity + 1))
                  }
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <i className="fa-solid fa-plus text-sm text-blue-500"></i>
                </button>
              </div>
            </div>

            {/* Total Price */}
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-700">
                  Total Price
                </span>
                <span className="text-2xl font-bold text-cyan-600">
                  {calculateTotalPrice().toLocaleString()} Fbu
                </span>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <p className="text-red-600">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={handleAddToCart}
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-4 rounded-2xl font-semibold hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <i className="fa-solid fa-shopping-cart"></i>
                <span>{isSubmitting ? "Adding..." : "Add to Cart"}</span>
              </button>
              <ShareButton product={product} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShareButton = ({ product }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Generate the product link with the product ID
  const productLink = `${window.location.origin}/market/${product.id}`;

  // Function to copy the link to the clipboard
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(productLink)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
      })
      .catch((err) => console.error("Failed to copy link:", err));
  };

  // Function to share on WhatsApp
  const shareOnWhatsApp = () => {
    const url = `https://api.whatsapp.com/send?text=Check out this product: ${productLink}`;
    window.open(url, "_blank");
  };

  // Function to share on Facebook
  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      productLink
    )}`;
    window.open(url, "_blank");
  };

  // Function to share on X (Twitter)
  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      productLink
    )}&text=Check out this product: ${product.name}`;
    window.open(url, "_blank");
  };

  // Function to share on Telegram
  const shareOnTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(
      productLink
    )}&text=Check out this product: ${product.name}`;
    window.open(url, "_blank");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-4 rounded-2xl font-semibold hover:scale-105 transition-transform duration-300 flex items-center space-x-2"
      >
        <i className="fas fa-share-alt"></i>
        <span>Share</span>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 p-2 min-w-48 z-10">
          <button
            onClick={shareOnWhatsApp}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-green-50 rounded-xl transition-colors text-green-600"
          >
            <i className="fab fa-whatsapp text-lg"></i>
            <span>WhatsApp</span>
          </button>
          <button
            onClick={shareOnFacebook}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-blue-50 rounded-xl transition-colors text-blue-600"
          >
            <i className="fab fa-facebook text-lg"></i>
            <span>Facebook</span>
          </button>
          <button
            onClick={shareOnTwitter}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-sky-50 rounded-xl transition-colors text-sky-600"
          >
            <i className="fab fa-twitter text-lg"></i>
            <span>Twitter</span>
          </button>
          <button
            onClick={shareOnTelegram}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-blue-50 rounded-xl transition-colors text-blue-500"
          >
            <i className="fab fa-telegram text-lg"></i>
            <span>Telegram</span>
          </button>
          <button
            onClick={copyToClipboard}
            className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 rounded-xl transition-colors text-gray-600"
          >
            <i className="fas fa-copy text-lg"></i>
            <span>{isCopied ? "Copied!" : "Copy Link"}</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Partners Component with Auto-scroll
const PartnersSection = ({ partners }) => {
  const scrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    // Get the total scrollable width
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const scrollWidth = scrollContainer.scrollWidth;
    const containerWidth = scrollContainer.clientWidth;
    const maxScrollPosition = scrollWidth - containerWidth;

    // Only set up scrolling if there's content to scroll
    if (maxScrollPosition <= 0) return;

    // Auto-scroll function
    const autoScroll = () => {
      if (isPaused) return;

      setScrollPosition((prevPosition) => {
        // When we reach the end, reset to start
        if (prevPosition >= maxScrollPosition) {
          return 0;
        }
        return prevPosition + 1; // Scroll 1px at a time for smooth movement
      });
    };

    // Apply the scroll position
    if (scrollContainer) {
      scrollContainer.scrollLeft = scrollPosition;
    }

    // Set up interval for smooth scrolling
    const scrollInterval = setInterval(autoScroll, 30);

    return () => clearInterval(scrollInterval);
  }, [scrollPosition, isPaused]);

  // Handle mouse enter/leave to pause/resume scrolling
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  return (
    <div className="partners-section">
      <h2 className="partners-title">Our Partners</h2>
      <div
        ref={scrollRef}
        className="partners-scroll-container"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Display partners twice to create a seamless loop effect */}
        {[...partners, ...partners].map((partner, index) => (
          <div key={`${partner.id}-${index}`} className="partner-card">
            <div className="partner-image-container">
              <ImageLoader
                src={partner.image}
                alt={partner.name}
                className="partner-image"
              />
            </div>
            <p className="partner-name">{partner.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Updated Category Display Component
const CategoryDisplay = ({ categories, onCategorySelect, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin"></div>
          <div
            className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-400 rounded-full animate-spin"
            style={{ animationDirection: "reverse", animationDuration: "1.5s" }}
          ></div>
        </div>
        <p className="mt-4 text-xl text-gray-600">Loading categories...</p>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <i className="fas fa-exclamation-circle text-3xl text-gray-400"></i>
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          No Categories Found
        </h3>
        <p className="text-gray-500">Please try again later</p>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Products</h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Explore our diverse range of products from talented African
          entrepreneurs
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {categories.map((category) => (
          <div
            key={category.name}
            onClick={() => onCategorySelect(category.name)}
            className="group cursor-pointer"
          >
            <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 overflow-hidden">
              <div className="relative aspect-square overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-500/20"></div>
                <ImageLoader
                  src={
                    category.image ||
                    `/images/categories/${category.name
                      .toLowerCase()
                      .replace(" ", "-")}.png`
                  }
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Category Icon */}
                <div className="absolute top-4 left-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <i className="fa-solid fa-tag text-white text-lg"></i>
                  </div>
                </div>
              </div>

              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                  {category.name}
                </h3>
                <div className="flex items-center justify-center text-cyan-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Explore Category</span>
                  <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductsList = ({
  products,
  category,
  onProductClick,
  onBackToCategories,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin"></div>
          <div
            className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-400 rounded-full animate-spin"
            style={{ animationDirection: "reverse", animationDuration: "1.5s" }}
          ></div>
        </div>
        <p className="mt-4 text-xl text-gray-600">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="py-8">
      {/* Category Header */}
      <div className="bg-white rounded-2xl p-2 md:p-3 shadow-lg mb-8">
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
          <div className="flex items-center space-x-3 md:space-x-4">
            <button
              onClick={onBackToCategories}
              className="flex items-center space-x-2 text-gray-600 hover:text-cyan-600 transition-colors duration-200 bg-gray-100 hover:bg-gray-200 px-3 py-2 md:px-4 rounded-full text-sm md:text-base"
            >
              <i className="fas fa-arrow-left"></i>
              <span className="hidden sm:inline">All Categories</span>
              <span className="sm:hidden">Back</span>
            </button>
            <div className="h-6 w-px bg-gray-300 hidden md:block"></div>
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 truncate">
              {category}
            </h2>
          </div>

          <div className="flex items-center justify-between md:justify-end space-x-4">
            <span className="text-sm md:text-base text-gray-600">
              {products.length} products
            </span>
            <div className="flex items-center space-x-2">
              <button className="p-2 text-gray-600 hover:text-cyan-600 transition-colors">
                <i className="fa-solid fa-filter"></i>
              </button>
              <button className="p-2 text-gray-600 hover:text-cyan-600 transition-colors">
                <i className="fa-solid fa-sort"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
            <i className="fas fa-box-open text-4xl text-gray-400"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-700 mb-2">
            No Products Found
          </h3>
          <p className="text-gray-500 mb-6">
            No products available in this category at the moment
          </p>
          <button
            onClick={onBackToCategories}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-3 rounded-full font-semibold hover:scale-105 transition-transform duration-300 flex items-center space-x-2"
          >
            <i className="fa-solid fa-arrow-left"></i>
            <span>Browse Other Categories</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 xl:gap-8">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onClick={onProductClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Main Marketplace Component
const Marketplace = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 12;
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([
    { id: 1, name: "DHL", image: "../images//dhl.jpg" },
    { id: 2, name: "Tuma Express", image: "./images/tuma.jpg" },
    { id: 3, name: "FEDEX", image: "../images/fedex.jpg" },
    {
      id: 4,
      name: "Lucky company (BENENTARE) ",
      image: "../images//luckybenent.jpg",
    },
  ]);

  // Fetch all products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/products`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch products: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      setProducts(data);

      // Create categories from products
      const categoryMap = data.reduce((acc, product) => {
        if (!product.category) return acc;

        const category = product.category;
        if (!acc[category]) {
          acc[category] = { name: category, count: 0 };
        }
        acc[category].count += 1;
        return acc;
      }, {});

      // Convert map to array and add "All" category
      const categoryArray = Object.values(categoryMap);
      categoryArray.unshift({
        name: "All Products",
        count: data.length,
      });

      setCategories(categoryArray);
    } catch (error) {
      console.error("Error fetching products:", error);
      setError(
        error.message || "Failed to load products. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch a specific product by ID
  const fetchProductById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/products/${id}`
      );
      if (!response.ok) {
        throw new Error(
          `Product not found: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      setSelectedProduct(data);

      // Also set the category so we show the correct product list
      if (data.category) {
        setSelectedCategory(data.category);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      setError(
        error.message || "Failed to load product. Please try again later."
      );
      // Don't navigate away immediately, let the user see the error
      setTimeout(() => navigate("/market"), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Load products on initial render
  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle product ID in URL
  useEffect(() => {
    if (productId) {
      fetchProductById(productId);
    } else {
      setSelectedProduct(null);
    }
  }, [productId]);

  // Reset to first page when category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  // Filter products based on search and selected category
  const filteredProducts = products.filter((product) => {
    // Check if the product exists and has the necessary properties
    if (!product || typeof product.name !== "string") return false;

    // Category filtering
    const categoryMatch =
      selectedCategory === "All Products" ||
      !selectedCategory ||
      (product.category && product.category === selectedCategory);

    // Search filtering
    const searchMatch =
      !searchQuery ||
      product.name.toLowerCase().includes(searchQuery.toLowerCase());

    // Availability filtering
    const availabilityMatch = product.availability === "available";

    return categoryMatch && searchMatch && availabilityMatch;
  });

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Handler for category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  // Handler for going back to categories
  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setCurrentPage(1);
  };

  // Handler for product selection
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  // Handler for product modal closure
  const handleCloseProductDetail = () => {
    setSelectedProduct(null);
    if (productId) {
      navigate("/market");
    }
  };

  // Function to retry fetching data
  const handleRetry = () => {
    if (productId) {
      fetchProductById(productId);
    } else {
      fetchProducts();
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="homeMainContainer">
        <Navbar />
        <div
          className="error-container"
          style={{
            marginTop: "100px",
            marginBottom: "100px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: "48px",
              color: "#f44336",
            }}
          ></i>
          <h2>THE MARKET IS NOT AVAILABLE!</h2>
          <p>We're updating the System</p>
          <button className="retry-btn btn2" onClick={handleRetry}>
            Try Again Later
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Show loading state for initial page load or product detail load
  if (loading && (products.length === 0 || productId)) {
    return (
      <div className="homeMainContainer">
        <Navbar />
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading {productId ? "product details" : "marketplace"}...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="homeMainContainer pt-0 pb-20 md:pb-0">
      <Navbar />
      <div className="wlcomMarket min-h-[50vh] relative">
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-blue-900/50 to-cyan-900/60 z-0"></div>
        <div className="txt gap-5">
          {/* Badge */}
          <div className="inline-flex items-center bg-white/50 backdrop-blur-sm border border-white/50 rounded-full px-6 py-2 mt-5">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse"></span>
            <span className="text-sm font-medium text-white">
              🌍 Supporting African Entrepreneurs
            </span>
          </div>
          <h1> Welcome at IBlotec Market </h1>

          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-32 h-32 bg-cyan-400/10 rounded-full blur-2xl animate-pulse"></div>
            <div
              className="absolute bottom-20 right-10 w-48 h-48 bg-purple-400/10 rounded-full blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
            <div
              className="absolute top-1/3 right-1/4 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-pulse"
              style={{ animationDelay: "2s" }}
            ></div>
          </div>

          <div className="mb-12">
            <SearchBar onSearch={setSearchQuery} />
          </div>
        </div>
      </div>

      {/* Conditionally render TrendingProducts */}
      {/* {!searchQuery && !selectedCategory && products.length > 0 && ( 
        <TrendingProducts
          products={products}
          onProductClick={handleProductSelect}
        />
      )}*/}

      <div className="relative min-h-screen">
        {/* Professional Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
          <div className="absolute inset-0 opacity-50"></div>
        </div>

        <div className="container-iblotec relative z-10">
          {/* Search Results Section */}
          {searchQuery && (
            <div className="py-8">
              <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/50 p-4 md:p-8 mb-8">
                <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 mb-6">
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                      Search Results
                    </h2>
                    <p className="text-sm md:text-base text-gray-600">
                      Found {filteredProducts.length} products for "
                      {searchQuery}"
                    </p>
                  </div>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 md:px-4 rounded-full transition-colors flex items-center justify-center space-x-2 text-sm md:text-base self-start md:self-auto"
                  >
                    <i className="fa-solid fa-times"></i>
                    <span className="hidden sm:inline">Clear Search</span>
                    <span className="sm:hidden">Clear</span>
                  </button>
                </div>

                {filteredProducts.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {currentProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onClick={handleProductSelect}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fa-solid fa-search text-2xl text-gray-400"></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      No Results Found
                    </h3>
                    <p className="text-gray-500">
                      Try adjusting your search terms
                    </p>
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="mt-8">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Categories or Products Section */}
          {!searchQuery && (
            <>
              {!selectedCategory ? (
                <CategoryDisplay
                  categories={categories}
                  onCategorySelect={handleCategorySelect}
                  isLoading={loading}
                />
              ) : (
                <>
                  <ProductsList
                    products={currentProducts}
                    category={selectedCategory}
                    onProductClick={handleProductSelect}
                    onBackToCategories={handleBackToCategories}
                    isLoading={loading}
                  />

                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  )}
                </>
              )}
            </>
          )}

          <ProductDetail
            product={selectedProduct}
            isOpen={!!selectedProduct}
            onClose={handleCloseProductDetail}
          />
        </div>

        {/* Seller CTA Section */}
        <div className="relative py-10 rounded-2xl overflow-hidden z-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url("/images/market.jpg")`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/85 to-blue-900"></div>
          </div>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 z-10">
            <div className="absolute top-10 left-10 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl animate-pulse"></div>
            <div
              className="absolute bottom-10 right-10 w-80 h-80 bg-purple-400/10 rounded-full blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
            <div
              className="absolute top-1/2 left-1/3 w-48 h-48 bg-blue-400/10 rounded-full blur-2xl animate-pulse"
              style={{ animationDelay: "2s" }}
            ></div>
          </div>

          <div className="container-iblotec relative z-10 text-center text-white z-10">
            <div className="max-w-5xl mx-auto">
              {/* Heading */}
              <h2 className="text-4xl md:text-5xl flex items-center justify-center gap-2 font-black mb-5 leading-tight">
                <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text text-transparent">
                  Ready to Start Selling?
                </span>
              </h2>

              {/* Description */}
              <p className="text-xl md:text-2xl text-gray-200 mb-4 max-w-3xl mx-auto leading-relaxed">
                Join other successful African entrepreneurs who are growing
                their businesses on IBloTec Market.
              </p>
              <p className="text-lg text-gray-300 mb-12 max-w-2xl mx-auto">
                Reach customers worldwide, increase your sales, and build your
                brand with our powerful e-commerce platform.
              </p>

              {/* Benefits */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-5 max-w-4xl mx-auto z-10">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-2 border border-white/10">
                  <i className="fa-solid fa-chart-line text-emerald-200 text-4xl"></i>
                  <h4 className="font-bold text-lg m-2 text-white">
                    Boost Sales
                  </h4>
                  <p className="text-gray-300 text-sm">
                    Increase revenue with our marketing tools
                  </p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-2 border border-white/10">
                  <i className="fa-solid fa-globe text-cyan-200 text-4xl"></i>
                  <h4 className="font-bold text-lg m-2 text-white">
                    Global Reach
                  </h4>
                  <p className="text-gray-300 text-sm">
                    Connect with customers worldwide
                  </p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-2 border border-white/10">
                  <i className="fa-solid fa-tools text-pink-300 text-4xl"></i>
                  <h4 className="font-bold text-lg m-2 text-white">
                    Easy Setup
                  </h4>
                  <p className="text-gray-300 text-sm">
                    Get started in minutes, not hours
                  </p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center z-10">
                <a
                  href="https://seller.iblotec.com"
                  className="group bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-12 py-3 rounded-full font-bold text-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl inline-flex items-center justify-center space-x-3 shadow-lg"
                >
                  <span className="text-white">Start Selling Today</span>
                  <i className="fa-solid fa-rocket group-hover:translate-x-1 transition-transform text-white"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile App Announcement */}
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
          <div className="container-iblotec text-center">
            <div className="max-w-4xl mx-auto">
              <div className="text-6xl mb-6">📱</div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Mobile App Coming Soon!
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Our mobile application will soon be available on both{" "}
                <span className="font-semibold text-green-600">
                  Google Play Store
                </span>{" "}
                and{" "}
                <span className="font-semibold text-blue-600">
                  Apple App Store
                </span>
                .
              </p>
              <p className="text-lg text-gray-600 mb-12">
                Stay tuned to enjoy a seamless IBloTec experience right from
                your phone!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  className="flex items-center space-x-3 bg-black text-white px-10 py-5 rounded-2xl font-semibold opacity-50 cursor-not-allowed"
                  disabled
                >
                  <div className="w-15 h-17 bg-blue-500 rounded-lg flex items-center justify-center">
                    <i className="fa-brands fa-google-play text-black text-5xl"></i>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-white">Get it on</div>
                    <div className="text-lg font-bold text-white">
                      Google Play
                    </div>
                  </div>
                </button>

                <button
                  className="flex items-center space-x-3 bg-black text-white px-10 py-5 rounded-2xl font-semibold opacity-50 cursor-not-allowed"
                  disabled
                >
                  <div className="w-15 h-15 bg-cyan-500 rounded-lg flex items-center justify-center">
                    <i className="fa-brands fa-apple text-black text-6xl"></i>
                  </div>
                  <div className="text-left">
                    <div className="text-xs  text-white">Download on the</div>
                    <div className="text-lg font-bold text-white">
                      App Store
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Partners Section 
        <PartnersSection partners={partners} />*/}
      </div>
      <Footer />
    </div>
  );
};

export default Marketplace;
